<template>
  <div
    class="studio-template"
    :class="{ 'studio-page': !$route.meta.hideTopBar }"
  >
    <div class="container-fluid text-center" v-if="isLoading">
      <HMSpinner class="mt-4" />
    </div>
    <div class="container">
      <div class="row">
        <div class="col-12">
          <StudentNavigation
            class="mt-4"
            v-if="!isLoading && $route.meta.showStudentNavigation"
          ></StudentNavigation>
        </div>
      </div>
    </div>
    <router-view v-if="!isLoading && showRouterView"></router-view>
    <ExpiredStudio v-if="!isLoading && !showRouterView" />
    <div class="container text-center studio-instagram" v-if="studio">
      <link
        :href="`https://fonts.googleapis.com/css2?family=${studio.default_font}:wght@400;500;700&display=swap`"
        rel="stylesheet"
      />
      <div class="mt-5"></div>
      <InstagramPhotosSlider
        :studioId="studio.id"
        :key="studio.id"
        v-if="!$route.meta.hideInstagramSlider"
      />
    </div>
    <HMCookieNotice
      class="cookie-notice"
      :class="{ 'cookie-notice--nav': !hideMobileNav }"
      v-if="!isPWA && !$route.meta.hideCookieNotice"
    />
    <StudioMobileNavigation
      class="studio-mobile-navigation"
      v-if="!hideMobileNav"
    />
    <ViewAsTeacherFooter class="view-as-teacher-footer" />
  </div>
</template>

<script>
import Vue from 'vue';
import InstagramPhotosSlider from '@/web/components/instagram/InstagramPhotosSlider';
import { mapGetters } from 'vuex';
import StudioUpcomingEventModal from '@/web/components/events/StudioUpcomingEventModal';
import ExpiredStudio from '@/web/components/shop/ExpiredStudio';
import { loadUserbackScript } from '@/web/utils/userback';
import RefreshData from '@/web/mixins/refreshData';
import store from '@/core/store';
import { getCurrentUrl } from '@/web/utils/url';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import { metaTitles } from '@/web/router/meta-utils';
import {
  initGoogleAnalyticsForStudio,
  initRewardful,
} from '@/web/services/analytics';
import StudentLogin from '@/web/components/signup/StudentLogin';
import { injectScript } from '@/web/utils/inject-script';
import StudioMobileNavigation from '@/web/components/navigation/StudioMobileNavigation';
import StudentNavigation from '@/web/components/navigation/StudentNavigation';
import HMCookieNotice from '@/web/components/app/HMCookieNotice';
import ViewAsTeacherFooter from '@/web/components/navigation/ViewAsTeacherFooter';

export default Vue.extend({
  name: 'StudioTemplate',
  metaInfo() {
    return {
      title: this.studio?.studio_name,
      meta: [
        ...metaTitles(this.studio?.studio_name),
        {
          vmid: 'og:image',
          property: 'og:image',
          content:
            this.studio?.cover_photo?.storage_url ??
            this.studio?.cover_photo?.embedded_image_url,
        },
        {
          vmid: 'og:url',
          property: 'og:url',
          content: getCurrentUrl(),
        },
        {
          vmid: 'twitter:card',
          name: 'twitter:card',
          content: 'summary_large_image',
        },
      ],
    };
  },
  mixins: [RefreshData],
  components: {
    InstagramPhotosSlider,
    ExpiredStudio,
    StudioMobileNavigation,
    HMCookieNotice,
    ViewAsTeacherFooter,
    StudentNavigation,
  },
  computed: {
    ...mapGetters({
      studio: 'studio/currentStudio',
      eventStartingSoon: 'studio/eventStartingSoon',
      googleAnalyticsCode: 'studio/googleAnalyticsCode',
      rewardfulApiKey: 'studio/rewardfulApiKey',
      showLoginModal: 'globals/showLoginModal',
      isPWA: 'globals/isPWA',
      showUserbackInStudio: 'globals/showUserbackInStudio',
    }),
    studioURL() {
      return getStudioURL(this.$route);
    },
    showRouterView() {
      if (!this.studio || this.$route.meta?.ignoreStudioExpiration) {
        return true;
      }
      return !this.studio.is_expired;
    },
    hideMobileNav() {
      return this.$route.meta.hideMobileNav;
    },
  },
  data() {
    return {
      isLoading: true,
      showNavDropdown: false,
    };
  },
  methods: {
    async loadData() {
      this.isLoading = this.studio?.studio_url != this.studioURL;
      try {
        const promises = [
          this.$store.dispatch('studio/getStudio', this.studioURL),
          this.$store.dispatch('studio/loadTranslations', this.studioURL),
        ];
        await Promise.all(promises);
        console.log('googleAnalyticsCode=', this.googleAnalyticsCode);
        if (this.googleAnalyticsCode) {
          initGoogleAnalyticsForStudio(
            this.googleAnalyticsCode,
            this.studioURL
          );
        }
        if (this.rewardfulApiKey) {
          initRewardful(this.rewardfulApiKey);
        }
        this.injectScripts();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: `Failed to load studio ${getStudioURL(this.$route)}`,
          text: error,
          duration: -1, // stay ON
        });
        this.$router.push({ name: 'studio-404' });
      } finally {
        this.isLoading = false;
      }
    },
    openLoginModal() {
      this.$modal.show(StudentLogin, {
        onLoginFn: () => {
          this.$store.dispatch('globals/setShowLoginModal', false);
          this.$toast.clear();
        },
      });
    },
    injectScripts() {
      if (this.studio.code_embed_enabled && this.studio.global_scripts) {
        injectScript(
          `studio-${this.studio.studio_url}-global-script`,
          this.studio.global_scripts
        );
      }
    },
    getElementByXpath(path) {
      return document.evaluate(
        path,
        document,
        null,
        XPathResult.FIRST_ORDERED_NODE_TYPE,
        null
      ).singleNodeValue;
    },
    relocateIntercom() {
      try {
        const intercomWidget =
          document.querySelector('.intercom-lightweight-app-launcher') ||
          this.getElementByXpath('//*[@id="intercom-container"]/div/div[2]');
        if (intercomWidget) {
          intercomWidget.style.bottom = '75px';
        }
        setTimeout(() => {
          this.relocateIntercom();
        }, 100);
      } catch (e) {
        // igonore
      }
    },
  },
  mounted() {
    if (this.showUserbackInStudio) {
      loadUserbackScript();
    }
    this.relocateIntercom();
    store.dispatch('studio/loadTranslations', this.studioURL);
  },
  watch: {
    eventStartingSoon: {
      handler(event) {
        if (event) {
          this.$modal.show(StudioUpcomingEventModal, {
            event: event.event,
            onClose: () => {
              this.$store.dispatch('studio/clearEventStartingSoon');
            },
          });
        }
      },
      immediate: true,
    },
    studioURL: {
      handler: 'loadData',
      immediate: true,
    },
    showLoginModal: {
      handler(show) {
        if (show) this.openLoginModal();
      },
      immediate: true,
    },
  },
});
</script>

<style lang="scss" scoped>
.studio-mobile-navigation {
  @media screen and (min-width: 768px) {
    display: none;
  }
}
.studio-template .cookie-notice--nav {
  @media screen and (max-width: 768px) {
    bottom: 70px;
  }
}

.view-as-teacher-footer {
  display: none;
  @media screen and (min-width: 768px) {
    display: block;
  }
}
</style>
